<template>
    <div class="row">
        <CModal
            v-show="assignModalVisible"
            @close="closeAssignModal"
            footer
        >
            <template v-slot:title>
                <h5>{{ assignType === 'reviewer' ? 'Assign Review Officer' : 'Assign Collection Officer' }}</h5>
            </template>
            <template v-slot:body>
                <div class="form-group">
                    <label>Loan Reference</label>
                    <input type="text" class="form-control" :value="selectedLoan ? selectedLoan.instant_reference : ''" disabled />
                </div>
                <div class="form-group">
                    <label>{{ assignType === 'reviewer' ? 'Review Officer' : 'Collection Officer' }}</label>
                    <select class="form-control" v-model="selectedOfficerId">
                        <option value="">Select officer</option>
                        <option
                            v-for="officer in assignType === 'reviewer' ? reviewOfficers : collectionOfficers"
                            :key="officer.id"
                            :value="officer.id"
                        >
                            {{ officer.first_name }} {{ officer.last_name }}
                            <template v-if="assignType === 'reviewer' && officer.review_officer_rank">
                                (Rank {{ officer.review_officer_rank }})
                            </template>
                            <template v-if="assignType === 'collector' && officer.collection_group">
                                ({{ officer.collection_group }})
                            </template>
                        </option>
                    </select>
                </div>
            </template>
            <template v-slot:footer>
                <button class="button" :disabled="assignLoading || !selectedOfficerId" @click="submitAssignment">
                    <span v-if="assignLoading">Assigning...</span>
                    <span v-else>Assign</span>
                </button>
                <button class="btn btn-secondary ml-2" @click="closeAssignModal">Cancel</button>
            </template>
        </CModal>

        <div class="col-md-12">
            <div class="card p-2">
                <div class="card-content">
                    <div class="card body p-md-3 p-2">
                        <CDataTable :items="loanProducts" :fields="fields" sorter>
                            <template #created_at="{ item }">
                                <td>{{ item.created_at | date }}</td>
                            </template>
                            <template #due_date="{ item }">
                                <td>{{ item.due_date | date }}</td>
                            </template>
                            <template #disbursed_date="{ item }">
                                <td>{{ item.disbursed_date | date }}</td>
                            </template>
                            <template #last_repayment_date="{ item }">
                                <td>{{ item.last_repayment_date | date }}</td>
                            </template>
                            <template #assigned_reviewer="{ item }">
                                <td>
                                    <span v-if="item.assigned_review_officer">
                                        {{ item.assigned_review_officer.first_name }} {{ item.assigned_review_officer.last_name }}
                                    </span>
                                    <span v-else class="text-muted">Unassigned</span>
                                </td>
                            </template>
                            <template #collection_officer="{ item }">
                                <td>
                                    <span v-if="item.collection_user_details">
                                        {{ item.collection_user_details.first_name }} {{ item.collection_user_details.last_name }}
                                    </span>
                                    <span v-else class="text-muted">Unassigned</span>
                                </td>
                            </template>
                            <template #action="{ item }">
                                <td class="d-flex flex-wrap" style="gap: 6px;">
                                    <router-link
                                        v-if="permissions.includes('view-instant-loan')"
                                        :to="{ name: 'LoanComponent', params: { slug: item.instant_reference } }"
                                        class="button"
                                    >
                                        View
                                    </router-link>
                                    <button
                                        v-if="permissions.includes('assign-loan-reviewer')"
                                        class="button btn-sm"
                                        @click="openAssignModal(item, 'reviewer')"
                                    >
                                        Assign Reviewer
                                    </button>
                                    <button
                                        v-if="permissions.includes('assign-loan-collection-officer')"
                                        class="button btn-sm"
                                        @click="openAssignModal(item, 'collector')"
                                    >
                                        Assign Collector
                                    </button>
                                </td>
                            </template>
                            <template #particulars="{ item }">
                                <td>
                                    <b>{{ `${item.user.first_name} ${item.user.last_name}` }}</b>
                                    <b>{{ item.user.phone_number }}</b><br>
                                    <b>{{ item.user.kyc.bvn }}</b>
                                    <b>{{ item.user.kyc.state.name }}</b>
                                </td>
                            </template>
                        </CDataTable>
                        <CPagination
                            :activePage.sync="next_page"
                            :pages="total ? total : 1"
                            :onActivePageChange="onPageChange()"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueDataTable from "@/components/VueDataTable";
import CCard from "@/components/CCard";
import CModal from "@/components/CModal";
import CDataTable from "@/components/CDataTable.vue";
import ApiService from "@/services/api.service";
import CPagination from "@/components/CPagination.vue";
import Swal from "sweetalert2";
import ErrorService from "@/services/error.service";

const fields = [
    "action",
    "particulars",
    "assigned_reviewer",
    "collection_officer",
    "due_date",
    "no_of_overdue_days",
    "instant_reference",
    "loan_status",
    "review_status",
    "disbursed_status",
    "tenure",
    "repayment_status",
    "requested_loan_amount",
    "approved_loan_amount",
    "charges",
    "created_at",
    "disbursed_date",
    "last_repayment_date",
];

export default {
    name: "LoanTable",
    components: {
        VueDataTable,
        CCard,
        CModal,
        CDataTable,
        CPagination
    },
    props: {
        loanProducts: {
            default: () => []
        },
        current: {
            default: 1
        },
        total: {
            default: 1
        },
        onPageChangeAction: {
            type: Function
        }
    },
    computed: {
        permissions() {
            return this.$store.getters.permissions;
        }
    },
    data() {
        return {
            fields,
            next_page: 1,
            assignModalVisible: false,
            assignType: "reviewer",
            selectedLoan: null,
            selectedOfficerId: "",
            assignLoading: false,
            reviewOfficers: [],
            collectionOfficers: []
        };
    },
    methods: {
        async getLoanOfficers() {
            if (
                !this.permissions.includes("assign-loan-reviewer") &&
                !this.permissions.includes("assign-loan-collection-officer")
            ) {
                return;
            }

            try {
                const response = await ApiService.get("loan/instant/loan_officers");
                this.reviewOfficers = response.data.data.review_officers || [];
                this.collectionOfficers = response.data.data.collection_officers || [];
            } catch (error) {
                console.log(error.response);
            }
        },
        openAssignModal(loan, type) {
            this.selectedLoan = loan;
            this.assignType = type;
            this.selectedOfficerId = type === "reviewer"
                ? (loan.review_officer_id || "")
                : (loan.collection_user_details ? loan.collection_user_details.id : "");
            this.assignModalVisible = true;
        },
        closeAssignModal() {
            this.assignModalVisible = false;
            this.selectedLoan = null;
            this.selectedOfficerId = "";
            this.assignType = "reviewer";
        },
        async submitAssignment() {
            if (!this.selectedLoan || !this.selectedOfficerId) {
                return;
            }

            const endpoint = this.assignType === "reviewer"
                ? "loan/instant/assign_reviewer"
                : "loan/instant/assign_collection_officer";

            const payload = this.assignType === "reviewer"
                ? {
                    instant_reference: this.selectedLoan.instant_reference,
                    review_officer_id: this.selectedOfficerId
                }
                : {
                    instant_reference: this.selectedLoan.instant_reference,
                    collection_officer_id: this.selectedOfficerId
                };

            try {
                this.assignLoading = true;
                await ApiService.post(endpoint, payload);
                this.assignLoading = false;
                this.closeAssignModal();
                Swal.fire({
                    title: "Success",
                    text: "Officer assigned successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2500
                });
                this.$emit("loanAssigned");
            } catch (error) {
                this.assignLoading = false;
                ErrorService.displayErrorAlert(error.response.data);
            }
        },
        onPageChange() {
            if (this.current != this.next_page) {
                this.$emit("onPageChangeAction", this.next_page);
            }
        }
    },
    mounted() {
        this.getLoanOfficers();
    }
};
</script>
